import { Link } from "react-router-dom";
import { MessageCircle, Mic } from "lucide-react";
import { DesktopFrame, PhoneFrame } from "@/components/site/Mocks";
import { DesktopFlow, PhoneFlow } from "@/components/site/DemoFlows";
import { SiteLanguageMenu } from "@/components/LanguageMenu";
import { t } from "@/i18n";

const STEPS = [
  ["01", "Record", "Video, voice, photos or notes. However you explain it."],
  ["02", "Approve", "SMBOI drafts the SOP. You check it and publish."],
  ["03", "Ask", "Your team asks in chat and gets cited answers."],
];
const USES = [
  [Mic, "Teach SMBOI", "So SMBOI can teach the whole team!", "teach-smboi"],
  [MessageCircle, "Ask SMBOI", "And SMBOI will always get an answer for you!", "ask-smboi"],
];

const Nav = () => (
  <header className="sticky top-0 z-40 bg-[var(--header-bg)] backdrop-blur-xl">
    <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
      <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 min-w-0 leading-tight">
        <Link to="/" className="font-bold text-lg tracking-tight" data-testid="site-logo">SMBOI</Link>
        <span className="text-xs sm:text-sm muted whitespace-nowrap" data-testid="site-author">{t("By:")}{" "}
          <a href="https://www.linkedin.com/in/jennyruanworks/" target="_blank" rel="noopener noreferrer"
            className="font-semibold text-[var(--text-primary)] underline decoration-[#0a66c2]/40 underline-offset-4 hover:text-[#0a66c2] hover:decoration-[#0a66c2] transition-colors"
            data-testid="site-author-linkedin-link">Jenny Ruan</a>
        </span>
      </span>
      <div className="flex items-center gap-1 sm:gap-2">
        <SiteLanguageMenu />
        <Link to="/sign-in" className="text-sm font-medium px-2 sm:px-3 py-2 whitespace-nowrap hover:opacity-70 transition-opacity" data-testid="landing-sign-in-button">{t("Log in")}</Link>
        <Link to="/sign-up" className="btn btn-primary btn-sm whitespace-nowrap max-[380px]:!px-3" data-testid="nav-get-started-button">{t("Start winning🏆")}</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-20">
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] rise" data-testid="hero-headline">
          SMBOI
        </h1>
        <span className="inline-flex items-center gap-3 rise d2">
          <Link to="/join/krusty-krab" className="btn !bg-[#f97316] hover:!bg-[#ea580c] !text-white !border-transparent shadow-[0_8px_24px_-8px_rgba(249,115,22,0.6)]" data-testid="landing-join-krusty-krab">{t("Try it: join the Krusty Krab as a new hire")}</Link>
        </span>
      </div>
      <p className="mt-6 text-lg md:text-xl muted max-w-xl rise d1" data-testid="hero-subline">
        {t("Every team needs an AI-powered small business boi to preserve and share the team's Original Intelligence💡")}
      </p>
    </div>
    <div className="relative mt-28 sm:mt-36 md:mt-28 lg:mt-16 grid grid-cols-12 items-end gap-6 rise d3">
      <div className="col-span-12 md:col-span-9 relative">
        <Link to="/join/krusty-krab" aria-label={t("Try it: join the Krusty Krab as a new hire")} title={t("Try it: join the Krusty Krab as a new hire")}
          className="absolute bottom-full right-[8%] md:right-0 z-20 w-[42%] sm:w-[34%] md:w-[30%] max-w-[300px] translate-y-[6%] origin-bottom transition-transform duration-300 hover:-translate-y-[2%] hover:scale-105 hover:-rotate-2 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#f97316]/50 rounded-3xl"
          data-testid="hero-mr-krabs-button">
          <img src="/avatars/mr-krabs-money.webp" alt="Mr. Krabs" className="w-full h-auto drop-shadow-[0_12px_18px_rgba(40,30,10,0.35)]" />
        </Link>
        <DesktopFrame><DesktopFlow /></DesktopFrame>
      </div>
      <div className="hidden md:block md:col-span-3 relative pb-10">
        <img src="/mascot.png" alt="SMBOI helper" className="absolute -top-20 -left-8 h-28 w-auto drop-shadow-lg z-10" data-testid="hero-mascot" />
        <PhoneFrame><PhoneFlow /></PhoneFrame>
      </div>
    </div>
  </section>
);

const How = () => (
  <section id="how" className="max-w-6xl mx-auto px-6 pt-24 pb-12 border-t border-[var(--border-color)]">
    <p className="mono-label">{t("How SMBOI works for you")}</p>
    <div className="mt-8 grid md:grid-cols-3 gap-10">
      {STEPS.map(([n, title, d]) => (
        <div key={n} data-testid={`how-step-${n}`}>
          <span className="serif text-4xl text-[var(--accent-primary)]">{n}</span>
          <h3 className="text-2xl font-semibold mt-2">{t(title)}</h3>
          <p className="muted mt-2">{t(d)}</p>
        </div>
      ))}
    </div>
  </section>
);

const Uses = () => (
  <section className="max-w-6xl mx-auto px-6 pt-12 pb-24 border-t border-[var(--border-color)]">
    <div className="grid sm:grid-cols-2 gap-px bg-[var(--border-color)] rounded-2xl overflow-hidden">
      {USES.map(([Icon, title, d, id]: any) => (
        <div key={id} className="bg-[var(--bg-surface)] p-7" data-testid={`use-case-${id}`}>
          <Icon size={22} className="text-[var(--accent-primary)]" />
          <h3 className="font-semibold text-lg mt-5">{t(title)}</h3>
          <p className="text-sm muted mt-1">{t(d)}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function Website() {
  return (
    <div className="min-h-screen" data-testid="website">
      <Nav />
      <Hero />
      <How />
      <Uses />
      <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-[var(--border-color)] flex justify-between text-sm faint" data-testid="landing-footer">
        <span>© {new Date().getFullYear()} SMBOI</span><span>{t("Small boi, original intelligence💡")}</span>
      </footer>
    </div>
  );
}
